import { ApiError, errorMessage } from '@restaurant/types';
import type { EN } from '@/i18n/errors/en';

// Core error plumbing (ApiError / errorMessage / unwrap / ErrorBody) now lives in
// @restaurant/types, shared with the storefront. Re-exported so existing
// `@/api/errors` imports are unchanged; the dashboard-only helpers below stay here.
export { ApiError, errorMessage, unwrap } from '@restaurant/types';
export type { ErrorBody } from '@restaurant/types';

/** Localized text for a thrown ApiError, looked up by its CODE + params (so a
 *  typed AppError like `reservation.shiftOverlap` renders its interpolated
 *  message). Unlike resolveErrorText this ignores the server's raw `message`
 *  (which equals the code for AppError responses). Non-ApiError → fallback. */
export function apiErrorText(err: unknown, messages: typeof EN, fallback: string): string {
  if (err instanceof ApiError) {
    return errorMessage({ code: err.code, params: err.params }, messages);
  }
  return fallback;
}

export function apiErrorTextTraced(err: unknown, messages: typeof EN, fallback: string): string {
  const base = apiErrorText(err, messages, fallback);
  if (err instanceof ApiError && err.traceId) return `${base} (#${err.traceId.slice(0, 8)})`;
  return base;
}

/** True when a thrown ApiError is a bare 403 with no server-provided code or
 *  message beyond NestJS's generic "Forbidden" - i.e. `ApiError.from` had to
 *  fall back to the `system.internal` code. A 403 that carried a real code or a
 *  specific message is NOT bare and keeps its own text. */
function isBareForbidden(err: ApiError): boolean {
  return (
    err.status === 403 &&
    err.code === 'system.internal' &&
    (err.detail === undefined || err.detail === 'Forbidden')
  );
}

/** Best human text for a thrown mutation/query error: the server's own message
 *  when it sent one, otherwise the localized text for its ErrorCode, otherwise
 *  a generic fallback. Pass the current locale's error messages (`useErrorT()`). */
export function resolveErrorText(err: unknown, messages: typeof EN): string {
  if (err instanceof ApiError) {
    // A bare 403 from the api - a NestJS `new ForbiddenException()` with no
    // `code` and only the generic "Forbidden" as its message - carries no useful
    // text: ApiError.from falls back to code 'system.internal' + detail
    // "Forbidden". Show the localized permission-denied string instead. When the
    // backend DID send a specific message (e.g. "Insufficient permissions to
    // manage order shifts.") its detail is that real text, so the block below
    // keeps surfacing it unchanged.
    if (isBareForbidden(err)) {
      return messages['system.forbidden'];
    }
    const base = err.detail ?? errorMessage({ code: err.code, params: err.params }, messages);
    // Append a short trace id so the user can quote it for support (it links to
    // the exact request in Loki). Only the first 8 chars - enough to find it.
    return err.traceId ? `${base} (#${err.traceId.slice(0, 8)})` : base;
  }
  return (messages as Record<string, string>)['system.internal'] ?? 'Something went wrong.';
}
